import { supabase } from '@/integrations/supabase/client';
import type { ZodType } from 'zod';

type EdgeAuthMode = 'session' | 'publishable' | 'none';

interface CallEdgeFunctionOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  auth?: EdgeAuthMode;
  allowStatuses?: number[];
}

interface EdgeFunctionResponse<TData> {
  data: TData;
  status: number;
}

interface EdgeFunctionErrorDetails {
  functionPath: string;
  status?: number;
  code?: string;
  details?: string;
  responseBody?: unknown;
}

export class EdgeFunctionClientError extends Error {
  readonly functionPath: string;
  readonly status?: number;
  readonly code?: string;
  readonly details?: string;
  readonly responseBody?: unknown;

  constructor(message: string, details: EdgeFunctionErrorDetails) {
    super(message);
    this.name = 'EdgeFunctionClientError';
    this.functionPath = details.functionPath;
    this.status = details.status;
    this.code = details.code;
    this.details = details.details;
    this.responseBody = details.responseBody;
  }
}

function buildFunctionUrl(functionPath: string, query?: CallEdgeFunctionOptions['query']): string {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  const normalizedPath = functionPath.replace(/^\/+/, '');
  const url = new URL(`${baseUrl}/functions/v1/${normalizedPath}`);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      url.searchParams.set(key, String(value));
    });
  }

  return url.toString();
}

async function resolveAuthHeader(auth: EdgeAuthMode): Promise<string | undefined> {
  if (auth === 'none') return undefined;

  if (auth === 'publishable') {
    const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    if (!key) throw new Error('Missing Supabase publishable key');
    return `Bearer ${key}`;
  }

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return `Bearer ${token}`;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  const responseWithOptionalJson = response as Response & { json?: () => Promise<unknown> };
  if (typeof responseWithOptionalJson.json === 'function') {
    try {
      return await responseWithOptionalJson.json();
    } catch {
      // Fall through to text parsing when json parsing is unavailable or invalid
    }
  }

  const responseWithOptionalText = response as Response & { text?: () => Promise<string> };
  if (typeof responseWithOptionalText.text !== 'function') {
    return {};
  }

  const text = await responseWithOptionalText.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function extractErrorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    const candidates = [record.error, record.message, record.details];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate;
      }
    }
  }
  return fallback;
}

export async function callEdgeFunction<TData = Record<string, unknown>>(
  functionPath: string,
  options: CallEdgeFunctionOptions = {},
): Promise<EdgeFunctionResponse<TData>> {
  const {
    method = 'POST',
    body,
    query,
    headers = {},
    auth = 'session',
    allowStatuses = [],
  } = options;

  const authorization = await resolveAuthHeader(auth);
  const response = await fetch(buildFunctionUrl(functionPath, query), {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(authorization ? { Authorization: authorization } : {}),
      ...headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const responseBody = (await parseResponseBody(response)) as TData;
  const isAllowedStatus = allowStatuses.includes(response.status);

  if (!response.ok && !isAllowedStatus) {
    throw new EdgeFunctionClientError(
      extractErrorMessage(responseBody, `Request failed with status ${response.status}`),
      {
        functionPath,
        status: response.status,
        responseBody,
      },
    );
  }

  return {
    data: responseBody,
    status: response.status,
  };
}

export async function callEdgeFunctionJson<TData = Record<string, unknown>>(
  functionPath: string,
  options: CallEdgeFunctionOptions = {},
): Promise<TData> {
  const response = await callEdgeFunction<TData>(functionPath, options);
  return response.data;
}

export async function callEdgeFunctionValidated<TData>(
  functionPath: string,
  schema: ZodType<TData>,
  options: CallEdgeFunctionOptions = {},
): Promise<TData> {
  const raw = await callEdgeFunctionJson<unknown>(functionPath, options);
  const parsed = schema.safeParse(raw);

  if (!parsed.success) {
    throw new EdgeFunctionClientError(
      `Invalid response shape from edge function: ${functionPath}`,
      {
        functionPath,
        code: 'EDGE_FUNCTION_SCHEMA_VALIDATION_FAILED',
        details: parsed.error.message,
        responseBody: raw,
      },
    );
  }

  return parsed.data;
}
